import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Loader as Loader2, MoreVertical, RefreshCw, Trash2, Unplug, Circle as XCircle, Database } from 'lucide-react';

import type { Site, WpUser } from './types';

interface SiteListRowProps {
  site: Site;
  loginLoading: boolean;
  activeDropdown: boolean;
  onToggleDropdown: () => void;
  onMagicLogin: (userId?: number) => void;
  onDisconnect: () => void;
  onDelete: () => void;
  onReconnect: () => void;
  openUserDropdown: boolean;
  onToggleUserDropdown: () => void;
  wpUsers: WpUser[];
  loadingUsers: boolean;
  onSetDefaultUser: (user: WpUser) => void;
}

export const SiteListRow: React.FC<SiteListRowProps> = ({
  site,
  loginLoading,
  activeDropdown,
  onToggleDropdown,
  onMagicLogin,
  onDisconnect,
  onDelete,
  onReconnect,
  openUserDropdown,
  onToggleUserDropdown,
  wpUsers,
  loadingUsers,
  onSetDefaultUser,
}) => {
  const navigate = useNavigate();
  const urlFormatted = site.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  
  const [backupsCount, setBackupsCount] = useState<number | null>(null);

  useEffect(() => {
    if (site.backupEnabled) {
      supabase.functions.invoke('r2-backups', {
        body: { action: 'LIST_BACKUPS', siteId: site.id }
      }).then(({ data, error }) => {
        if (!error && data?.backups) {
          setBackupsCount(data.backups.length);
        } else {
          setBackupsCount(0);
        }
      });
    }
  }, [site.id, site.backupEnabled]);

  const pluginUpdatesCount = Array.isArray(site.last_plugins_data) ? site.last_plugins_data.filter((p: any) => p.has_update).length : 0;
  const themeUpdatesCount = Array.isArray(site.last_themes_data) ? site.last_themes_data.filter((t: any) => t.has_update).length : 0;
  const coreUpdatesCount = site.last_core_info?.wordpress?.has_update ? 1 : 0;
  const updatesCount = pluginUpdatesCount + themeUpdatesCount + coreUpdatesCount;

  let errorsCount = 0;
  if (site.status === 'error') errorsCount += 1;
  if (site.uptimeStatus === 'down') errorsCount += 1;
  const sslData = site.last_core_info?.ssl;
  if (sslData && typeof sslData === 'object' && !sslData.enabled) errorsCount += 1;

  const isLocalSite = urlFormatted.endsWith('.local') || urlFormatted.includes('localhost') || urlFormatted.startsWith('127.0.0.1');
  const isUptimeDown = site.uptimeStatus === 'down' || site.uptime_status === 'down';
  const isUptimeUp = site.uptimeStatus === 'up' || site.uptime_status === 'up' || (site.status === 'connected' && !isUptimeDown);

  return (
    <tr className="hover:bg-muted/30 transition-colors group cursor-pointer" onClick={() => navigate(`/sites/${site.id}/overview`)}>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center flex-shrink-0 border border-border/50 overflow-hidden shadow-sm">
            {!isLocalSite ? (
              <img
                src={`https://www.google.com/s2/favicons?domain=${urlFormatted}&sz=64`}
                alt=""
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <Globe className={`w-5 h-5 text-muted-foreground ${!isLocalSite ? 'hidden' : ''}`} />
          </div>
          <div className="flex flex-col min-w-0 gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground text-sm truncate" title={site.name}>{site.name}</span>
              {site.status !== 'connected' && (
                <div className="w-2 h-2 rounded-full flex-shrink-0 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" title="Disconnected" />
              )}
            </div>
            <a href={site.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline truncate" onClick={(e) => e.stopPropagation()}>
              {urlFormatted}
            </a>
          </div>
        </div>
      </td>

      <td className="px-6 py-4 text-center">
        {updatesCount > 0 ? (
          <div className="inline-flex items-center justify-center gap-1.5 bg-purple-100/50 dark:bg-purple-900/20 text-[#593d9b] dark:text-purple-300 px-2.5 py-1 rounded-full text-[11px] font-bold border border-purple-200 dark:border-purple-800/30">
            <RefreshCw className="w-3 h-3" /> {updatesCount}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>

      <td className="px-6 py-4 text-center">
        {isUptimeDown ? (
          <div className="inline-flex items-center justify-center gap-1.5 bg-destructive/10 text-destructive px-2.5 py-1 rounded-full text-[11px] font-bold border border-destructive/20 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-destructive shadow-[0_0_6px_rgba(239,68,68,0.5)]" />
            Down
          </div>
        ) : isUptimeUp ? (
          <div className="inline-flex items-center justify-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 rounded-full text-[11px] font-bold border border-emerald-200 dark:border-emerald-800/30 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]" />
            Up
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>

      <td className="px-6 py-4 text-center">
        {errorsCount > 0 ? (
          <div className="inline-flex items-center justify-center gap-1.5 bg-destructive/10 text-destructive px-2.5 py-1 rounded-full text-[11px] font-bold border border-destructive/20">
            <XCircle className="w-3 h-3" /> {errorsCount}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>

      <td className="px-6 py-4 text-center">
        {site.backupEnabled ? (
          <div className="inline-flex items-center justify-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 rounded-full text-[11px] font-bold border border-emerald-200 dark:border-emerald-800/30">
            <Database className="w-3 h-3" /> {backupsCount !== null ? `${backupsCount} Backup${backupsCount === 1 ? '' : 's'}` : 'Loading...'}
          </div>
        ) : (
          <div className="inline-flex items-center justify-center gap-1.5 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2.5 py-1 rounded-full text-[11px] font-bold border border-amber-200 dark:border-amber-800/30">
            <XCircle className="w-3 h-3" /> Inactive
          </div>
        )}
      </td>

      <td className="px-6 py-4 text-center">
        <div className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
          <div className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2.5 py-1 rounded-full text-[11px] font-bold border border-blue-200 dark:border-blue-800/30">
            <svg className="w-3 h-3" viewBox="0 0 512 512" fill="currentColor">
              <path d="M61.7 169.4l101.5 278C92.2 413 43.3 340.2 43.3 256c0-30.9 6.6-60.1 18.4-86.6zm337.9 75.9c0-26.3-9.4-44.5-17.5-58.7-10.8-17.5-21.4-32.3-21.4-50.5 0-21.4 17.5-40.4 40.4-40.4 1.3 0 2.7 .2 4 .4-47-45.7-111.4-74.2-182.2-74.2-56.5 0-108.6 17.5-151.7 47.5l98.9 278.4 84.7-242.4h-39.7V75h132v29.6h-39.7l69.9 201 27.5-91.8c7.4-23.4 11.4-42.3 11.4-56.1zm-84.6 24.3L237.9 494.4c5.8 1 11.8 1.6 18.1 1.6 74.9 0 137.9-38.4 174.6-96.2l-115.6-130.2zm-28 80l-63 177.3c41.3 16 86.8 16 128.1 0l-65.1-177.3zM256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0z"/>
            </svg> {site.wpVersion || '6.4.3'}
          </div>
          <div className="inline-flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 px-2.5 py-1 rounded-full text-[11px] font-bold border border-indigo-200 dark:border-indigo-800/30">
            <svg className="w-3 h-3" viewBox="0 0 512 512" fill="currentColor">
              <path d="M256 32C114.6 32 0 125.1 0 240c0 102 96.1 186.7 220 204.5V480h72v-35.5c123.9-17.9 220-102.5 220-204.5C512 125.1 397.4 32 256 32zm-73.4 254.3c-2.3 11.9-12.7 20.3-25.1 20.3h-25.5l11.4-58.4h25.4c12.3 0 20.4 8.2 18.3 19.9l-4.5 18.2zm113-41c-2.3 11.9-12.7 20.3-25.1 20.3h-25.4l11.4-58.4h25.4c12.3 0 20.4 8.2 18.3 19.9l-4.6 18.2zm112.5 41c-2.3 11.9-12.7 20.3-25.1 20.3H358l-12.2 62.4h-35.8l23.5-120.7h54c25 0 42.7 17.6 37.9 41.6l-7.3 36.4zm-308.2-3.1c-13.6 16.9-35.2 25.8-63.1 25.8H73.3l-12.2 62.4H25.3l23.5-120.7h59.1c28.3 0 48.9 9.8 54.1 26.6 2.3 7.3 1.2 16.1-2.1 26.3zM218 206h-35.8l-5 25.6h35.8l5-25.6zm10.7-54.8h35.8l-18.4 94h-35.8l18.4-94z"/>
            </svg> 8.1.2
          </div>
        </div>
      </td>

      <td className="px-6 py-4 text-center">
        <div className="relative flex items-center justify-center h-[34px]" onClick={(e) => e.stopPropagation()}>
          {site.status !== 'connected' ? (
            <button
              onClick={onReconnect}
              className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-colors border-none shadow-sm flex items-center justify-center gap-1.5 h-full whitespace-nowrap"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reconnect
            </button>
          ) : loginLoading ? (
            <div className="bg-primary/80 h-full w-[60px] rounded-lg flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-white" />
            </div>
          ) : (
            <div className="inline-flex h-full rounded-lg overflow-visible shadow-sm relative">
              <button
                onClick={() => onMagicLogin()}
                title={site.defaultWpUserName ? `Login as ${site.defaultWpUserName}` : 'Magic Login'}
                className="bg-primary text-white text-xs font-semibold pl-3 pr-2.5 rounded-l-lg hover:bg-primary/90 transition-colors flex items-center gap-1.5 h-full"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
                <span>Login</span>
              </button>
              <button
                onClick={onToggleUserDropdown}
                className="bg-primary hover:bg-primary/90 text-white w-7 flex-shrink-0 flex items-center justify-center rounded-r-lg border-l border-white/20 h-full"
                title="Choose user"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <AnimatePresence>
                {openUserDropdown && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 6 }}
                    className="absolute right-0 origin-top-right top-full mt-1.5 w-64 bg-popover border border-border rounded-xl shadow-xl z-[200] overflow-hidden text-left"
                  >
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Login as</p>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {loadingUsers ? (
                        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                      ) : wpUsers.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-5">No eligible users found</p>
                      ) : (
                        wpUsers.map(user => (
                          <div key={user.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 transition-colors group/row">
                            <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full flex-shrink-0 bg-muted" onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.display_name)}&size=32&background=random`; }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-foreground truncate">{user.display_name}</p>
                              <p className="text-[10px] text-muted-foreground capitalize">{user.roles[0] ?? 'user'}</p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                              <button
                                onClick={() => onSetDefaultUser(user)}
                                title="Set as default"
                                className={`p-1 rounded transition-colors ${site.defaultWpUserId === user.id ? 'text-yellow-500' : 'text-muted-foreground hover:text-yellow-500'}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={site.defaultWpUserId === user.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                              </button>
                              <button
                                onClick={() => onMagicLogin(user.id)}
                                title="Login as this user"
                                className="p-1 rounded text-muted-foreground hover:text-primary transition-colors"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></svg>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </td>

      <td className="pl-2 pr-6 py-4 text-right">
        <div className="relative inline-block text-left">
          <button
            className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={(e) => { e.stopPropagation(); onToggleDropdown(); }}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          <AnimatePresence>
            {activeDropdown && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="absolute right-0 bottom-full mb-2 w-48 bg-popover rounded-xl shadow-xl border border-border overflow-hidden z-[100] origin-bottom-right"
              >
                <div className="p-1 flex flex-col gap-1">
                  {site.status === 'connected' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDisconnect(); }}
                      className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors flex items-center gap-2 font-medium rounded-lg"
                    >
                      <Unplug className="w-4 h-4" /> Disconnect Site
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2 font-medium rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" /> Remove Site
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </td>
    </tr>
  );
};
